/* eslint-disable @typescript-eslint/no-floating-promises */

import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Spacer } from 'foundations-components/components/Spacer';
import { View } from 'foundations-components/components/View';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import getIn from 'transmute/getIn';
import { getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getIsCallIncoming, getShouldShowActiveCallState } from 'calling-internal-common/widget-status/operators/getCallState';
import { getIsTabCall } from 'calling-ui-library/utils/widgetDetailsOperators';
import updateCallingReadyState from 'calling-global-api/methods/updateCallingReadyState';
import openCallingWidget from 'calling-global-api/methods/openCallingWidget';
import preloadCallingWidget from 'calling-global-api/methods/preloadCallingWidget';
import { getPropertyName, getObjectId, getObjectTypeId, getPhoneNumberProperties } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { getSource } from 'calling-lifecycle-internal/operators/from-number/FromNumberGetters';
// @ts-expect-error not typed
import SearchableCalleeList from 'calling-ui-library/callee-select/components/SearchableCalleeList';
import CallSettingsIcon from 'calling-ui-library/callee-select/components/CallSettingsIcon';

// @ts-expect-error not typed
import CurrentSettingsInfo from './CurrentSettingsInfo';
import { getCallableObjectAndAssociations } from 'calling-lifecycle-internal/callee-omnibus/operators/calleeOmnibusResultGetters';
import { READY, UNINITIALIZED, LOADING } from 'calling-internal-common/widget-status/constants/CallWidgetStates';
import { CALL_FROM_PHONE } from 'calling-internal-common/constants/CallMethods';
import { PhoneNumberSources } from 'calling-internal-common/constants/PhoneNumberSources';
import { useWidgetDetails } from 'calling-cross-tab-library/external/useWidgetDetails';
import { getCallStatus } from 'calling-cross-tab-library/appData/operators/widgetDetailsOperators';
import { getIsProviderHubSpot, getIsTwilioBasedCallProvider } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import { getHubSpotCallingEnabled, getInstalledCallingApplications, getPhoneNumbers } from '../../callee-omnibus/operators/settingsOmnibusResultGetters';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { hasOutboundCallingAccess } from 'calling-orchestration-shared-library/hub-info/utils/hasCallingFeature';
import { CALLEE_NUMBERS } from 'calling-integration-sdk-support/viewport/constants/changeProviderButtonLocations';
import ChangeProviderButtonWrapper from './ChangeProviderButtonWrapper';
import { getIsThirdPartyCallingPopoverInUse } from 'calling-internal-common/utils/isThirdPartyCallingPopoverInUse';
import ProviderName from './ProviderName';
import { getHasMultipleProviders } from '../utils/getHasMultipleProviders';
import { triggerThirdPartyOutboundCall } from 'calling-ui-library/calling-remote-redesign/utils/triggerThirdPartyOutboundCall';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ButtonWrapper = styled.div.withConfig({
  displayName: "CallSettingsNumbers__ButtonWrapper",
  componentId: "sc-1kolzsi-0"
})(["> button{width:100%;}&:hover{background-color:", ";}"], ({
  theme
}) => theme.color['do-not-use-calypso-light']);
const CallSettingsNumbers = props => {
  const {
    appIdentifier,
    asyncCalleeOmnibus,
    asyncSettingsOmnibus,
    widgetState,
    selectedCallProvider,
    selectedFromNumber,
    selectedCallMethod,
    gates,
    objectTypeId,
    subjectId,
    onSetCallStatus,
    onSetThirdPartyActive,
    onCallButtonClick,
    threadId
  } = props;
  const {
    widgetDetails
  } = useWidgetDetails();
  const crossTabCallStatus = getCallStatus(widgetDetails);
  const openPropertiesPanel = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_ALL_PROPERTIES_PANEL);
  const handleOnOpenNotes = () => {
    openCallingWidget({
      callingProvider: selectedCallProvider
    });
    CommunicatorLogger.log('Calling_DropDownInteractions', {
      action: 'open calling widget',
      source: appIdentifier
    });
    onCallButtonClick();
  };
  const handleCallButtonClick = ({
    numberIdentifier,
    callableObject
  }) => {
    const phoneNumberProperties = getPhoneNumberProperties(callableObject);
    const numberPropertyName = getPropertyName(numberIdentifier);
    const objectId = getObjectId(callableObject);
    const callableObjectTypeId = getObjectTypeId(callableObject);
    if (!getIsTwilioBasedCallProvider(props.selectedCallProvider)) {
      triggerThirdPartyOutboundCall({
        objectId,
        objectTypeId: callableObjectTypeId,
        phoneNumberProperties,
        numberPropertyName,
        gates,
        selectedCallProvider,
        threadId
      });
    } else {
      openCallingWidget({
        toSubject: {
          objectId,
          objectTypeId: callableObjectTypeId,
          phoneNumberProperties,
          numberPropertyName,
          threadId
        },
        startCall: true,
        callingProvider: selectedCallProvider
      });
    }
    onCallButtonClick();
    CommunicatorLogger.log('Calling_DropDownInteractions', {
      action: 'start call from dropdown',
      source: appIdentifier
    });
  };
  const handleAddPropertyClick = ({
    callableObject
  }) => {
    if (getIsThirdPartyCallingPopoverInUse(gates, selectedCallProvider)) {
      openPropertiesPanel({
        objectTypeId: getObjectTypeId(callableObject),
        objectId: String(getObjectId(callableObject)),
        initialSearchTerm: 'phone'
      });
    } else {
      openCallingWidget({
        toSubject: {
          objectId: getObjectId(callableObject),
          objectTypeId: getObjectTypeId(callableObject),
          phoneNumberProperties: getPhoneNumberProperties(callableObject)
        },
        startCall: false,
        addPhoneNumber: true,
        callingProvider: selectedCallProvider
      });
    }
    CommunicatorLogger.log('Calling_DropDownInteractions', {
      action: 'add "to" number property',
      source: appIdentifier
    });
    onCallButtonClick();
  };
  useEffect(() => {
    preloadCallingWidget();
    const callStatusFromWidgetState = getIn(['callStatus'], widgetState);
    const shouldSendUpdateReadyState = [READY, UNINITIALIZED, LOADING].includes(callStatusFromWidgetState);
    if (shouldSendUpdateReadyState) {
      updateCallingReadyState({
        subjectId,
        objectTypeId
      });
    }
  }, [objectTypeId, widgetState, subjectId]);
  const calleeOmnibusData = getData(asyncCalleeOmnibus);
  const calleesData = calleeOmnibusData && getCallableObjectAndAssociations(calleeOmnibusData);
  const shouldShowActiveCallState = getShouldShowActiveCallState(widgetState.get('callStatus'));
  const isActiveCallTab = getIsTabCall(widgetDetails);
  const isThirdPartyActive = getIn(['isThirdPartyActive'], widgetState);
  const isHubspotNumber = getSource(selectedFromNumber) === PhoneNumberSources.HUBSPOT_NUMBER;
  const isHubSpotNumberAndCallFromPhone = isHubspotNumber && selectedCallMethod === CALL_FROM_PHONE;
  const noCallingOutboundAccessWithHubSpotProvider = !hasOutboundCallingAccess() && getIsProviderHubSpot(selectedCallProvider);
  const isCallTabActive = (getShouldShowActiveCallState(crossTabCallStatus) || getIsCallIncoming(crossTabCallStatus)) && isActiveCallTab;
  const disabledTooltipMessage = isHubSpotNumberAndCallFromPhone ? 'callee-phone-numbers.callFromPhoneAndHSProvidedMessage' : noCallingOutboundAccessWithHubSpotProvider ? 'callSettingsOptions.freeSeatHubSpotCallingBlocked' : isCallTabActive ? 'callSettingsOptions.callActiveOutboundCallBlocked' : null;
  const settingsOmnibusData = getData(asyncSettingsOmnibus);
  const installedCallingApplications = getInstalledCallingApplications(settingsOmnibusData);
  const hasMultipleProviders = useMemo(() => getHasMultipleProviders({
    installedCallingApplicationsLength: (installedCallingApplications === null || installedCallingApplications === void 0 ? void 0 : installedCallingApplications.length) || 0,
    fromNumbers: getPhoneNumbers(settingsOmnibusData),
    isHubSpotEnabled: getHubSpotCallingEnabled(selectedCallProvider)
  }), [installedCallingApplications, settingsOmnibusData, selectedCallProvider]);
  const showChangeProviderButton = useMemo(() => {
    return hasMultipleProviders;
  }, [hasMultipleProviders]);
  const showOpenCallOptions = useMemo(() => {
    return getIsTwilioBasedCallProvider(selectedCallProvider) || !selectedCallProvider.supportsInboundCalling;
  }, [selectedCallProvider]);
  const showCurrentSettingsInfo = useMemo(() => getIsTwilioBasedCallProvider(selectedCallProvider), [selectedCallProvider]);
  const showProviderName = useMemo(() => {
    return !getIsTwilioBasedCallProvider(selectedCallProvider) && hasMultipleProviders;
  }, [selectedCallProvider, hasMultipleProviders]);
  return /*#__PURE__*/_jsxs(View, {
    maxBlockSize: "400px",
    overflow: "auto",
    children: [/*#__PURE__*/_jsx(SearchableCalleeList, {
      appIdentifier: appIdentifier,
      calleesData: calleesData,
      onSelectToNumber: handleCallButtonClick,
      disabled: !!disabledTooltipMessage,
      canEdit: true,
      onAddPropertyClick: handleAddPropertyClick,
      showAddPropertyButton: true,
      CallSettingsIcon: CallSettingsIcon,
      disabledTooltipMessage: disabledTooltipMessage
    }), /*#__PURE__*/_jsxs(View, {
      position: "sticky",
      insetBlockEnd: "0",
      backgroundColor: "fill-surface-default",
      borderBlockStartStyle: "solid",
      borderBlockStartWidth: "100",
      borderColor: "border-core-disabled",
      children: [showCurrentSettingsInfo && /*#__PURE__*/_jsx(CurrentSettingsInfo, {
        appIdentifier: appIdentifier,
        asyncSettingsOmnibus: asyncSettingsOmnibus,
        selectedCallProvider: selectedCallProvider,
        selectedFromNumber: selectedFromNumber,
        selectedCallMethod: selectedCallMethod,
        widgetState: widgetState,
        onSetCallStatus: onSetCallStatus,
        onSetThirdPartyActive: onSetThirdPartyActive,
        disabled: isThirdPartyActive || shouldShowActiveCallState,
        showFromNumberDropdown: getIsTwilioBasedCallProvider(selectedCallProvider)
      }), showProviderName && /*#__PURE__*/_jsx(ProviderName, {
        selectedCallProvider: selectedCallProvider
      }), showOpenCallOptions &&
      /*#__PURE__*/
      /* @bend-expect trellis-tokenized */
      _jsx(ButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, {
          use: "link",
          onClick: handleOnOpenNotes,
          "data-selenium-test": "open-call-notes-button",
          marginInlineStart: "0",
          paddingInline: "400",
          paddingBlock: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "callSettingsWidget.openSettingsButton"
          })
        })
      }), showChangeProviderButton && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ChangeProviderButtonWrapper, {
          providerName: selectedCallProvider.name,
          location: CALLEE_NUMBERS,
          gates: gates
        }), /*#__PURE__*/_jsx(Spacer, {
          space: "100"
        })]
      })]
    })]
  });
};
export default CallSettingsNumbers;